"use client";

import { createContext, useContext } from "react";
import type { User } from "firebase/auth";

export interface AppAuthState {
  /** Lowercased role groups from Firebase custom claims */
  userGroups: string[];
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isTopup: boolean;
  isSubscriber: boolean;
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  authError: string | null;
  displayName: string;
  email: string | undefined;
  hasMobile: boolean | null;
  picture: string | undefined;
  /** Rank frame image path based on user XP */
  rankFrame: string;
  /** Raw OIDC auth object (deprecated, kept for backward compat) */
  oidcAuth?: any;
  /** Firebase User object (null when signed out) */
  firebaseUser: User | null;
  /** Call to sign out */
  logout: () => Promise<void>;
  /** Fetch a bearer token from the current Firebase session */
  getToken: () => Promise<string>;
  /** Re-fetch the profile from the backend (call after modal login) */
  refreshSession: () => Promise<void>;
  /** Update picture directly so we don't have to wait for an API refetch */
  updatePicture: (url: string) => void;
}

const defaultState: AppAuthState = {
  userGroups: [],
  isAdmin: false,
  isSuperAdmin: false,
  isTopup: false,
  isSubscriber: false,
  isAuthenticated: false,
  isAuthLoading: true,
  authError: null,
  displayName: "Player",
  email: undefined,
  hasMobile: null,
  picture: undefined,
  rankFrame: "/assets/rank/1.webp",
  oidcAuth: null,
  firebaseUser: null,
  logout: async () => {},
  getToken: async () => "",
  refreshSession: async () => {},
  updatePicture: () => {},
};

export const AppAuthContext = createContext<AppAuthState>(defaultState);

/**
 * Unified auth hook that reads from a shared Context so ALL components
 * (Navbar, LoginModal, etc.) see the same auth state.
 * After a Firebase sign-in + refreshSession(), every consumer re-renders
 * automatically — no page reload needed.
 */
export function useAppAuth(): AppAuthState {
  return useContext(AppAuthContext);
}
